import { FC, useEffect, useState } from "react";

import { DonationFormCategory, OrganizationAssistantCampaignType, TicketingFormCategory } from "@simplyk/common";

import { useServiceEntryPanel } from "../Context/ServiceEntryPanelContext";
import { readCampaignType } from "../helpers/readCampaignType";
import { NavigationButton } from "../NavigationButton/NavigationButton";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import type { AssistantToolRendererProps } from "@/features/Assistant/contexts/AssistantConfigContext";
import { useCreateCampaignType } from "@/features/Campaigns/helpers/useCreateCampaignType";

export const ConnectedCampaignTypeButton: FC<AssistantToolRendererProps> = ({ part }) => {
  const { t } = useTranslate();
  const panel = useServiceEntryPanel();
  const { logAmplitudeEvent } = useAmplitude();
  const { createCampaignType, isCreatable, isSweepstakesCountry, raffleComplianceDisclaimerDialog } =
    useCreateCampaignType({ source: "organization_assistant" });
  const campaignType = readCampaignType(part.output);
  const onRecommend = panel?.onRecommend;
  const [isCreating, setIsCreating] = useState(false);

  // The preview next to the chat shows the type the assistant recommended last.
  useEffect(() => {
    if (campaignType && onRecommend) {
      onRecommend(campaignType);
    }
  }, [campaignType, onRecommend]);

  if (!campaignType || !isCreatable(campaignType)) {
    return null;
  }

  const labels: Record<OrganizationAssistantCampaignType, string> = {
    [DonationFormCategory.DonationForm]: t("dashboard", "formNew.serviceEntryB.assistant.create.DonationForm"),
    [DonationFormCategory.PeerToPeerCampaign]: t(
      "dashboard",
      "formNew.serviceEntryB.assistant.create.PeerToPeerCampaign"
    ),
    [TicketingFormCategory.Event]: t("dashboard", "formNew.serviceEntryB.assistant.create.Event"),
    [TicketingFormCategory.Lottery]: isSweepstakesCountry
      ? t("dashboard", "formNew.serviceEntryB.assistant.create.LotteryWithSweepstakes")
      : t("dashboard", "formNew.serviceEntryB.assistant.create.Lottery"),
    [TicketingFormCategory.Auction]: t("dashboard", "formNew.serviceEntryB.assistant.create.Auction"),
    [TicketingFormCategory.MembershipV2]: t("dashboard", "formNew.serviceEntryB.assistant.create.MembershipV2"),
    [TicketingFormCategory.Shop]: t("dashboard", "formNew.serviceEntryB.assistant.create.Shop"),
    signUpForms: t("dashboard", "formNew.serviceEntryB.assistant.create.signUpForms"),
  };

  const create = async () => {
    setIsCreating(true);
    try {
      logAmplitudeEvent(AmplitudeEvents.ServiceEntryAssistantCreateClicked, { form_type: campaignType });
      await createCampaignType(campaignType);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <NavigationButton label={labels[campaignType]} isLoading={isCreating} onClick={create} />
      {raffleComplianceDisclaimerDialog}
    </>
  );
};
